import { Dispatch, SetStateAction, useState } from "react";

type SetValue<T> = Dispatch<SetStateAction<T>>;

function useLocalStorage<T>(key: string, initialValue: T): [T, SetValue<T>] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (jsonToObject(item) as T) : initialValue;
    } catch (err) {
      console.warn(`Error in fetching data from local storage key <${key}>`, err);
      return initialValue;
    }
  });

  const setValueToStorage: SetValue<T> = (value) => {
    if (typeof window == "undefined") {
      return;
    }
    try {
      const newValue = value instanceof Function ? value(storedValue) : value;
      window.localStorage.setItem(key, JSON.stringify(newValue));
      setStoredValue(newValue);
    } catch (err) {
      console.warn(`Error in save data in local storage key ${key}:`, err);
    }
  };

  return [storedValue, setValueToStorage];
}

export default useLocalStorage;

function jsonToObject<T>(value: string): T | undefined {
  try {
    return value === "undefined" ? undefined : JSON.parse(value ?? "");
  } catch {
    console.warn("parsing error on", { value });
    return undefined;
  }
}

export const STORAGE_KEYS = {
  STORED_ASSIGNATION: "STORED_ASSIGNATION",
  STORED_PROJECT: "STORED_PROJECT",
  STORED_DATE: "STORED_DATE",
  MAX_STORED_DATE: "MAX_STORED_DATE",
  STORED_KNOWLEDGE_BASE_CATEGORY: "STORED_KNOWLEDGE_BASE_CATEGORY",
  STORED_KNOWLEDGE_BASE_CODE: "STORED_KNOWLEDGE_BASE_CODE",
  STORED_KNOWLEDGE_BASE: "STORED_KNOWLEDGE_BASE",
  STORED_KNOWLEDGE_BASE_SEARCH_ALL: "STORED_KNOWLEDGE_BASE_SEARCH_ALL",
  STORED_ASSIGNED_TO_ME: "STORED_ASSIGNED_PROJECT",
  STORED_ONLY_EXTERNAL: "STORED_ONLY_EXTERNAL",
  STORED_SHOW_ARCHIVED_PROJECTS: "STORED_SHOW_ARCHIVED_PROJECTS",
  STORED_ASSIGNATIONS: "STORED_ASSIGNATIONS",
  INVOICE_DRAFT: "INVOICE_DRAFT",
  LAST_INVOICE_NUMBER: "LAST_INVOICE_NUMBER",
  THEME_MODE: "THEME_MODE",
  WORKLOG_VIEW: "WORKLOG_VIEW",
};

export const removeItem = (key: string | undefined) => {
  if (typeof window != "undefined" && typeof key != "undefined") {
    if (key in window.localStorage) {
      localStorage.removeItem(key);
    }
  }
};
